import { useCallback, useEffect, useState } from "react";

export type TCategoryForSelection = {
  categoryId: string;
  categoryName: string;
};

type TUseCategorySelectionOptions = {
  /** Called when selection changes (e.g. for parent filter sync) */
  onSelectionChange?: (params: { selectedCategoryIds: string[]; isAllSelected: boolean }) => void;
  /** Called when All is selected or a category is toggled (e.g. reset filter buttons) */
  onFilterReset?: () => void;
};

export function useCategorySelection(_categories: TCategoryForSelection[], options?: TUseCategorySelectionOptions) {
  const { onSelectionChange, onFilterReset } = options ?? {};

  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(true);

  useEffect(() => {
    onSelectionChange?.({ selectedCategoryIds, isAllSelected });
  }, [isAllSelected, onSelectionChange, selectedCategoryIds]);

  const handleAllSelect = useCallback(() => {
    setIsAllSelected(true);
    setSelectedCategoryIds([]);
    onFilterReset?.();
  }, [onFilterReset]);

  const handleCategoryToggle = useCallback(
    (categoryId: string) => {
      onFilterReset?.();
      setSelectedCategoryIds((prev) => {
        const next = prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId];
        setIsAllSelected(next.length === 0);
        return next;
      });
    },
    [onFilterReset]
  );

  /** Reset selection without calling onFilterReset (e.g. when switching to filter button) */
  const resetSelection = useCallback(
    (options?: { skipFilterReset?: boolean }) => {
      setIsAllSelected(true);
      setSelectedCategoryIds([]);
      if (!options?.skipFilterReset) {
        onFilterReset?.();
      }
    },
    [onFilterReset]
  );

  return {
    selectedCategoryIds,
    isAllSelected,
    handleAllSelect,
    handleCategoryToggle,
    resetSelection,
  };
}
